import { Box, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { ComplianceTypes } from '@/constants';
import { certCpd2026, certCpe2026 } from '@/images';
import {
  NameFields,
  onboardingActions,
  selectOnboardingStepInputs,
  selectOnboardingUserCompliance,
  STEP_IDS,
  useAppDispatch,
  useAppSelector,
  useFetchUserProfileQuery,
} from '@/store';

import { styles } from '../styles';
import { Footer } from './Footer';
import { Header } from './Header';
import { ModalSubtitle } from './ModalSubtitle';
import { ModalTitle } from './ModalTitle';

export const NameForm = () => {
  const dispatch = useAppDispatch();

  const stepInputs = useAppSelector(selectOnboardingStepInputs);
  const userCompliance = useAppSelector(selectOnboardingUserCompliance);

  const { data: userProfile } = useFetchUserProfileQuery();

  const [isFormTouched, setIsFormTouched] = useState(false);
  const { value: form } = stepInputs[STEP_IDS.name];

  useEffect(() => {
    if (!isFormTouched && userProfile) {
      dispatch(
        onboardingActions.updateStepInputs({
          step: STEP_IDS.name,
          input: {
            first: userProfile.firstName || '',
            last: userProfile.lastName || '',
          },
        }),
      );
    }
  }, [dispatch, isFormTouched, userProfile]);

  const handleChange = (id: keyof NameFields, value: string) => {
    if (!isFormTouched) setIsFormTouched(true);
    dispatch(
      onboardingActions.updateStepInputs({
        step: STEP_IDS.name,
        input: {
          ...form,
          [id]: value,
        },
      }),
    );
  };

  return (
    <Box sx={styles.formContainer}>
      <Header Title={<Title />} Subtitle={<Subtitle />} />
      <Box sx={styles.formPrimarySection}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 1 },
            zIndex: 1,
            py: 2,
          }}>
          <TextField
            id={'first'}
            label="First Name"
            variant="outlined"
            onChange={e => handleChange('first', e.target.value)}
            value={form.first}
            fullWidth
          />
          <TextField
            id={'last'}
            label="Last Name"
            variant="outlined"
            onChange={e => handleChange('last', e.target.value)}
            value={form.last}
            fullWidth
          />
        </Box>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            overflow: 'hidden',
            flexGrow: 1,
            pt: 3,
            justifyContent: 'center',
          }}>
          <Box
            sx={{
              position: 'relative',
            }}>
            <Box
              component="img"
              src={
                userCompliance === ComplianceTypes.CPE
                  ? certCpe2026
                  : certCpd2026
              }
              alt={`Example ${userCompliance} certificate`}
              sx={{
                width: 400,
                height: 'auto',
                boxShadow: '0px -4px 20px 0px #0000001A',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 18.5,
                top: userCompliance === ComplianceTypes.CPE ? 84 : 104,
                right: 0,
                backgroundColor: 'white',
                width: 'fit-content',
              }}>
              <Typography variant="body3SemiBold">{`${form.first} ${form.last}`}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

const Title = () => <ModalTitle>Enter your legal name.</ModalTitle>;

const Subtitle = () => (
  <ModalSubtitle>
    It is important that you enter your legal first and last name in order for
    us to receive and report accurate certificates.
  </ModalSubtitle>
);
